import { useState, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, getCountFromServer, doc, deleteDoc, updateDoc, query, orderBy, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { functions, db, storage, auth } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { logAdminActivity } from '../services/adminActivityLog';
import { usePermissions } from '../hooks/usePermissions';
import { CITIES } from '../utils/cities';
import {
  BellIcon,
  UserGroupIcon,
  TruckIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  ListBulletIcon
} from '@heroicons/react/24/outline';

export default function Notifications() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  // Form state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [targetType, setTargetType] = useState('all');
  // Optional city filter for driver notifications. Empty = no city filter.
  // When set, only drivers whose serviceCities array contains 'all' or any
  // of these keys, or who haven't set serviceCities yet, receive the push.
  const [selectedCities, setSelectedCities] = useState([]);
  const [activeDriversCount, setActiveDriversCount] = useState(null);
  const [loadingActiveCount, setLoadingActiveCount] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Image upload state
  const [uploadMethod, setUploadMethod] = useState('url'); // 'url' or 'upload'
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  
  // User selection
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Stats
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalDrivers: 0,
    totalCustomers: 0,
    usersWithTokens: 0
  });
  const [loadingStats, setLoadingStats] = useState(true);

  // All Notifications
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [editingNotif, setEditingNotif] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [sweepingOrphans, setSweepingOrphans] = useState(false);
  const { isMasterAdmin, user: currentAdmin } = useAuth();
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission('delete_notifications');

  useEffect(() => {
    fetchStats();
    fetchActiveDriversCount();
  }, []);

  useEffect(() => {
    if (targetType === 'specific') {
      fetchUsers();
    }
    if (targetType === 'drivers' && activeDriversCount === null) {
      fetchActiveDriversCount();
    }
  }, [targetType]);

  const fetchActiveDriversCount = async () => {
    try {
      setLoadingActiveCount(true);
      // Server-side count() — instant and free of document downloads.
      const agg = await getCountFromServer(
        query(
          collection(db, 'users'),
          where('type', '==', 'driver'),
          where('status', '==', 'active'),
        ),
      );
      setActiveDriversCount(agg.data().count);
    } catch (err) {
      console.error('Error fetching active drivers count:', err);
    } finally {
      setLoadingActiveCount(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const getNotificationStats = httpsCallable(functions, 'getNotificationStats');
      const response = await getNotificationStats();
      
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      // Run the two type-scoped queries in parallel; both are indexed on
      // `type` so Firestore filters at the server (no client scan of admins
      // / agents / etc.). We still drop `deleted` docs in case any slipped
      // through with that status.
      const [driversSnap, customersSnap] = await Promise.all([
        getDocs(query(collection(db, 'users'), where('type', '==', 'driver'))),
        getDocs(query(collection(db, 'users'), where('type', '==', 'customer'))),
      ]);
      const usersList = [...driversSnap.docs, ...customersSnap.docs]
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => u.status !== 'deleted');

      setUsers(usersList);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoadingNotifications(true);
      const q = query(collection(db, 'notifications'), orderBy('sentAt', 'desc'));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        sentAt: d.data().sentAt?.toDate?.() ?? null,
      }));
      setNotifications(list);
      setSelectedIds([]);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleDeleteNotification = async (id) => {
    try {
      setDeletingId(id);
      await deleteDoc(doc(db, 'notifications', id));
      setNotifications(prev => prev.filter(n => n.id !== id));
      setConfirmDeleteId(null);
    } catch (err) {
      console.error('Error deleting notification:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev =>
      prev.length === notifications.length ? [] : notifications.map(n => n.id)
    );
  };

  const handleSweepOrphans = async () => {
    if (!isMasterAdmin) return;
    if (!window.confirm(
      'Clean up orphan notification recipients?\n\n' +
      'This deletes recipient docs whose parent notification was already deleted, ' +
      'which removes the phantom unread badge in the apps. Safe to run.'
    )) return;
    try {
      setSweepingOrphans(true);
      const sweep = httpsCallable(functions, 'sweepOrphanNotificationRecipients');
      const res = await sweep({});
      const { scanned = 0, orphansDeleted = 0 } = res.data || {};
      alert(`Done. Scanned ${scanned} recipient docs, deleted ${orphansDeleted} orphans.`);
    } catch (err) {
      console.error('Sweep orphans error:', err);
      alert(`Failed: ${err.message}`);
    } finally {
      setSweepingOrphans(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setBulkDeleting(true);
      await Promise.all(selectedIds.map(id => deleteDoc(doc(db, 'notifications', id))));
      setNotifications(prev => prev.filter(n => !selectedIds.includes(n.id)));
      setSelectedIds([]);
    } catch (err) {
      console.error('Bulk delete error:', err);
    } finally {
      setBulkDeleting(false);
    }
  };

  const openEdit = (notif) => {
    setEditingNotif(notif);
    setEditTitle(notif.title || '');
    setEditBody(notif.body || '');
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingNotif) return;
    setEditError(null);
    try {
      setSavingEdit(true);
      const notifRef = doc(db, 'notifications', editingNotif.id);
      await updateDoc(notifRef, {
        title: editTitle.trim(),
        body: editBody.trim(),
      });
      setNotifications(prev =>
        prev.map(n =>
          n.id === editingNotif.id
            ? { ...n, title: editTitle.trim(), body: editBody.trim() }
            : n
        )
      );
      setEditingNotif(null);
    } catch (err) {
      console.error('Error updating notification:', err);
      setEditError(err.code === 'permission-denied'
        ? 'Permission denied — check Firestore security rules for the notifications collection.'
        : err.message || 'Failed to update notification.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type - only allow raster images (no SVG)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      setError('Only JPG, PNG, GIF, and WebP images are allowed. SVG files are not supported.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setSelectedImage(file);
    setError(null); // Clear any previous errors
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setImageUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadImage = async () => {
    if (!selectedImage) return null;

    try {
      setUploadingImage(true);
      
      // Check if user is authenticated
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('You must be logged in to upload images');
      }
      
      console.log('Current user:', currentUser.uid);
      console.log('Uploading image:', selectedImage.name);
      
      // Create unique filename
      const timestamp = Date.now();
      const fileName = `notifications/${timestamp}_${selectedImage.name}`;
      const storageRef = ref(storage, fileName);
      
      console.log('Storage path:', fileName);
      
      // Upload file
      await uploadBytes(storageRef, selectedImage);
      
      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);
      
      console.log('Upload successful! URL:', downloadURL);
      
      return downloadURL;
    } catch (err) {
      console.error('Error uploading image:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      throw new Error('Failed to upload image: ' + err.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSendNotification = async () => {
    try {
      // Validation
      if (!title.trim() || !body.trim()) {
        setError('Title and message are required');
        return;
      }

      if (targetType === 'specific' && selectedUsers.length === 0) {
        setError('Please select at least one user');
        return;
      }

      setLoading(true);
      setError(null);
      setResult(null);

      let finalImageUrl = imageUrl.trim();

      // Upload image if user selected file upload method
      if (uploadMethod === 'upload' && selectedImage) {
        finalImageUrl = await uploadImage();
      }

      let finalTargetType = targetType;
      let finalTargetUserIds = targetType === 'specific' ? selectedUsers : [];

      // Drivers target = active drivers only (status == 'active'), with
      // optional city filter applied client-side. We fetch active drivers
      // directly and switch to 'specific' target so the Cloud Function
      // pushes to only that list.
      if (targetType === 'drivers') {
        const hasCityFilter = selectedCities.length > 0;
        const driversSnap = await getDocs(
          query(
            collection(db, 'users'),
            where('type', '==', 'driver'),
            where('status', '==', 'active'),
          ),
        );
        const filteredIds = driversSnap.docs
          .filter((d) => {
            if (!hasCityFilter) return true;
            // City filter — match if:
            //   • serviceCities missing/empty (legacy driver)
            //   • serviceCities includes 'all'
            //   • serviceCities intersects with the admin's selection
            const sc = d.data().serviceCities;
            const legacy = !Array.isArray(sc) || sc.length === 0;
            const all = Array.isArray(sc) && sc.includes('all');
            const overlap = Array.isArray(sc) &&
              sc.some((c) => selectedCities.includes(String(c).toLowerCase()));
            return legacy || all || overlap;
          })
          .map((d) => d.id);

        if (filteredIds.length === 0) {
          setError('No active drivers matched the selected filters');
          setLoading(false);
          return;
        }

        finalTargetType = 'specific';
        finalTargetUserIds = filteredIds;
      }

      const sendNotification = httpsCallable(functions, 'sendNotification');

      const response = await sendNotification({
        title: title.trim(),
        body: body.trim(),
        imageUrl: finalImageUrl || null,
        targetType: finalTargetType,
        targetUserIds: finalTargetUserIds
      });

      setResult(response.data);

      if (response.data.success) {
        logAdminActivity({
          actor: currentAdmin,
          action: 'send_notification',
          targetType: 'notification',
          targetLabel: `"${title.trim()}" → ${finalTargetType}${Array.isArray(finalTargetUserIds) ? ` (${finalTargetUserIds.length} users)` : ''}`,
        });
      }

      // Clear form on success
      if (response.data.success) {
        setTitle('');
        setBody('');
        setImageUrl('');
        removeImage();
        setSelectedUsers([]);
        fetchStats();
      }

    } catch (err) {
      console.error('Error sending notification:', err);
      setError(err.message || 'Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    const filteredUsers = getFilteredUsers();
    setSelectedUsers(filteredUsers.map(u => u.id));
  };

  const clearSelection = () => {
    setSelectedUsers([]);
  };

  const getFilteredUsers = () => {
    if (!searchQuery) return users;
    
    const query = searchQuery.toLowerCase();
    return users.filter(user => 
      user.name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.phoneNumber?.includes(query)
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Push Notifications</h1>
          <p className="text-gray-600 mt-1">Send notifications to users instantly</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setShowNotifications(!showNotifications);
              if (!showNotifications && notifications.length === 0) {
                fetchNotifications();
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
          >
            <ListBulletIcon className="w-5 h-5" />
            <span className="font-medium">All Notifications</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Active Drivers</p>
              <p className="text-3xl font-bold mt-2">
                {loadingActiveCount
                  ? '...'
                  : activeDriversCount ?? '—'}
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <TruckIcon className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Customers</p>
              <p className="text-3xl font-bold mt-2">{stats.totalCustomers}</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <UserIcon className="w-6 h-6" />
            </div>
          </div>
        </div>

      </div>

      {/* All Notifications Panel */}
      {showNotifications && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold text-gray-900">All Notifications</h2>
              {notifications.length > 0 && (
                <label className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-600 select-none">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === notifications.length && notifications.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  Select All
                </label>
              )}
            </div>
            <div className="flex items-center gap-2">
              {canDelete && selectedIds.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  <TrashIcon className="w-4 h-4" />
                  {bulkDeleting ? 'Deleting...' : `Delete (${selectedIds.length})`}
                </button>
              )}
              {isMasterAdmin && (
                <button
                  onClick={handleSweepOrphans}
                  disabled={sweepingOrphans}
                  className="text-sm text-orange-600 hover:text-orange-700 font-medium disabled:opacity-50"
                  title="Delete recipient docs whose parent notification was already deleted"
                >
                  {sweepingOrphans ? 'Cleaning...' : 'Clean Orphans'}
                </button>
              )}
              <button
                onClick={fetchNotifications}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Refresh
              </button>
            </div>
          </div>

          {loadingNotifications ? (
            <div className="text-center py-8">
              <div className="inline-block w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-gray-600 mt-2">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No notifications found</p>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
              {notifications.map(notif => (
                <div key={notif.id} className={`border rounded-lg p-4 transition-colors ${selectedIds.includes(notif.id) ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(notif.id)}
                      onChange={() => toggleSelect(notif.id)}
                      className="mt-1 w-4 h-4 text-blue-600 rounded shrink-0 cursor-pointer"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900">{notif.title}</h3>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          notif.status === 'sent'
                            ? 'bg-green-100 text-green-800'
                            : notif.status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {notif.status}
                        </span>
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {notif.targetType}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{notif.body}</p>
                      {notif.imageUrl && (
                        <img
                          src={notif.imageUrl}
                          alt=""
                          className="mt-2 w-24 h-24 rounded-lg object-cover border border-gray-200"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                      <div className="flex items-center gap-4 mt-2 flex-wrap">
                        <span className="text-xs text-green-600">✓ {notif.totalSent ?? 0} sent</span>
                        {(notif.totalFailed ?? 0) > 0 && (
                          <span className="text-xs text-red-600">✗ {notif.totalFailed} failed</span>
                        )}
                        {notif.sentAt && (
                          <span className="text-xs text-gray-400">
                            {notif.sentAt.toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => openEdit(notif)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      {canDelete && (
                        confirmDeleteId === notif.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDeleteNotification(notif.id)}
                              disabled={deletingId === notif.id}
                              className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 font-medium disabled:opacity-50"
                            >
                              {deletingId === notif.id ? '...' : 'Confirm'}
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 font-medium"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDeleteId(notif.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit Notification Modal */}
      {editingNotif && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Edit Notification</h2>
              <button
                onClick={() => setEditingNotif(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                maxLength={100}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              />
              <p className="text-xs text-gray-400 mt-1">{editTitle.length}/100</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Message</label>
              <textarea
                value={editBody}
                onChange={e => setEditBody(e.target.value)}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">{editBody.length}/500</p>
            </div>

            {editError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {editError}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setEditingNotif(null)}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={savingEdit || !editTitle.trim() || !editBody.trim()}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
        {/* Target Type Selection */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Send To
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { value: 'drivers', label: 'Active Drivers', icon: TruckIcon, color: 'green', count: activeDriversCount ?? stats.totalDrivers },
              { value: 'customers', label: 'Customers', icon: UserIcon, color: 'purple', count: stats.totalCustomers },
              { value: 'specific', label: 'Specific Users', icon: UserIcon, color: 'orange', count: selectedUsers.length }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setTargetType(option.value)}
                className={`p-4 rounded-xl border-2 transition-all ${
                  targetType === option.value
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <option.icon className={`w-8 h-8 mx-auto mb-2 ${
                  targetType === option.value ? 'text-blue-600' : 'text-gray-400'
                }`} />
                <p className={`text-sm font-semibold ${
                  targetType === option.value ? 'text-blue-900' : 'text-gray-600'
                }`}>
                  {option.label}
                </p>
                <p className={`text-xs mt-1 ${
                  targetType === option.value ? 'text-blue-700' : 'text-gray-500'
                }`}>
                  {option.value === 'specific' && targetType === 'specific' 
                    ? `${option.count} selected`
                    : `${option.count} users`}
                </p>
              </button>
            ))}
          </div>

          {/* Drivers panel — active drivers only, optional city filter */}
          {targetType === 'drivers' && (
            <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-xs text-gray-600">
                Notification will be sent to{' '}
                <strong>
                  {loadingActiveCount
                    ? '...'
                    : activeDriversCount ?? stats.totalDrivers}
                </strong>{' '}
                active driver(s) (status = active).
              </p>

              {/* Service-area (city) filter */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-700">
                    Cities filter <span className="font-normal text-gray-500">(optional)</span>
                  </label>
                  {selectedCities.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedCities([])}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  Send only to drivers whose service area matches at least one selected city. Drivers
                  who chose "All cities" (or haven't picked yet) are always included.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {CITIES.map((c) => {
                    const isOn = selectedCities.includes(c.key);
                    return (
                      <button
                        type="button"
                        key={c.key}
                        onClick={() =>
                          setSelectedCities((prev) =>
                            isOn ? prev.filter((k) => k !== c.key) : [...prev, c.key],
                          )
                        }
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                          isOn
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                        }`}
                      >
                        {c.en}
                      </button>
                    );
                  })}
                </div>
                {selectedCities.length > 0 && (
                  <p className="text-xs text-blue-700 mt-2">
                    {selectedCities.length} city{selectedCities.length === 1 ? '' : 'ies'} selected.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* User Selection (if specific) */}
        {targetType === 'specific' && (
          <div className="border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between mb-4">
              <label className="text-sm font-semibold text-gray-700">
                Select Users ({selectedUsers.length} selected)
              </label>
              <div className="flex gap-2">
                <button
                  onClick={selectAllUsers}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Select All
                </button>
                <button
                  onClick={clearSelection}
                  className="text-sm text-gray-600 hover:text-gray-700 font-medium"
                >
                  Clear
                </button>
              </div>
            </div>

            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
              {loadingUsers ? (
                <div className="p-8 text-center">
                  <div className="inline-block w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-sm text-gray-600 mt-2">Loading users...</p>
                </div>
              ) : getFilteredUsers().length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No users found
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {getFilteredUsers().map(user => (
                    <label
                      key={user.id}
                      className="flex items-center gap-3 p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => toggleUserSelection(user.id)}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user.name || 'Unnamed User'}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            user.type === 'driver' 
                              ? 'bg-green-100 text-green-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {user.type}
                          </span>
                          <p className="text-xs text-gray-500 truncate">
                            {user.email || user.phoneNumber}
                          </p>
                          {!user.fcmToken && (
                            <span className="text-xs text-orange-600" title="No FCM token">⚠️</span>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notification Content */}
        <div className="space-y-4 border-t border-gray-200 pt-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Notification Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter notification title..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              maxLength={100}
            />
            <p className="text-xs text-gray-500 mt-1">{title.length}/100 characters</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Message *
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Enter notification message..."
              rows={5}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base resize-none"
              maxLength={500}
            />
            <p className="text-xs text-gray-500 mt-1">{body.length}/500 characters</p>
          </div>

          {/* Image Section with Upload/URL Toggle */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <PhotoIcon className="w-4 h-4 inline mr-1" />
              Notification Image (Optional)
            </label>
            
            {/* Toggle between URL and Upload */}
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => {
                  setUploadMethod('url');
                  removeImage();
                }}
                className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  uploadMethod === 'url'
                    ? 'bg-blue-100 text-blue-700 border-2 border-blue-500'
                    : 'bg-gray-100 text-gray-600 border-2 border-gray-200 hover:bg-gray-200'
                }`}
              >
                <PhotoIcon className="w-4 h-4 inline mr-1" />
                Image URL
              </button>
              <button
                type="button"
                onClick={() => {
                  setUploadMethod('upload');
                  setImageUrl('');
                }}
                className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  uploadMethod === 'upload'
                    ? 'bg-blue-100 text-blue-700 border-2 border-blue-500'
                    : 'bg-gray-100 text-gray-600 border-2 border-gray-200 hover:bg-gray-200'
                }`}
              >
                <ArrowUpTrayIcon className="w-4 h-4 inline mr-1" />
                Upload Image
              </button>
            </div>

            {/* URL Input */}
            {uploadMethod === 'url' && (
              <div>
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                />
                <p className="text-xs text-gray-500 mt-1">Enter the URL of an image hosted online</p>
                
                {/* URL Preview */}
                {imageUrl && (
                  <div className="mt-3 relative">
                    <img
                      src={imageUrl}
                      alt="URL Preview"
                      className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden flex-col items-center justify-center w-full h-48 bg-gray-100 rounded-lg border-2 border-gray-200">
                      <PhotoIcon className="w-12 h-12 text-gray-400" />
                      <p className="text-sm text-gray-500 mt-2">Invalid image URL</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Upload Input */}
            {uploadMethod === 'upload' && (
              <div>
                {!imagePreview ? (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleImageSelect}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="flex flex-col items-center justify-center w-full px-4 py-8 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <ArrowUpTrayIcon className="w-12 h-12 text-gray-400 mb-3" />
                      <p className="text-sm font-medium text-gray-700">
                        Click to upload image
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        PNG, JPG, GIF, WebP up to 5MB (no SVG)
                      </p>
                    </label>
                  </div>
                ) : (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                    <div className="mt-2 p-2 bg-gray-100 rounded text-xs text-gray-600 truncate">
                      {selectedImage?.name}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Send Button */}
        <button
          onClick={handleSendNotification}
          disabled={loading || uploadingImage || !title.trim() || !body.trim()}
          className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-bold text-lg text-white transition-all shadow-lg ${
            loading || uploadingImage || !title.trim() || !body.trim()
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:shadow-xl transform hover:scale-[1.02]'
          }`}
        >
          {loading || uploadingImage ? (
            <>
              <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>{uploadingImage ? 'Uploading Image...' : 'Sending Notification...'}</span>
            </>
          ) : (
            <>
              <PaperAirplaneIcon className="w-6 h-6" />
              <span>Send Notification Now</span>
            </>
          )}
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <XCircleIcon className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-red-900">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-600 hover:text-red-800"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}

      {/* Success Result */}
      {result && result.success && (
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <CheckCircleIcon className="w-10 h-10 text-green-600 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-xl font-bold text-green-900">
                🎉 Notification Sent Successfully!
              </h3>
              <p className="text-base text-green-700 mt-2">{result.message}</p>
              <div className="flex flex-wrap gap-4 mt-4 text-sm">
                <div className="flex items-center gap-2 bg-green-100 px-4 py-2 rounded-lg">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                  <span className="text-green-900">
                    <strong>Delivered:</strong> {result.totalSent}
                  </span>
                </div>
                {result.totalFailed > 0 && (
                  <div className="flex items-center gap-2 bg-orange-100 px-4 py-2 rounded-lg">
                    <ExclamationTriangleIcon className="w-5 h-5 text-orange-600" />
                    <span className="text-orange-900">
                      <strong>Failed:</strong> {result.totalFailed}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}